import type { SxProps, Theme } from "@mui/material";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { Fragment, useEffect, useMemo, useRef } from "react";
import type { ChartOfAccountsItem } from "../../../../../../../../../api/types/svTypes.ts";
import { getEmptyConditionsCaption } from "../../chartOfAccounts/helpers/variablePropertyItem.helper";
import type { CustomMeasureReturnType } from "../hooks/useCustomMeasure";
import type { FormulaNode } from "../utilities/fillNodes";
import { findNodeByTokenData } from "../utilities/findNodeByTokenData";
import { orderCustomMeasureConditions } from "../utilities/variables.utils.ts";
import AddCustomMeasureConditions from "./AddCustomMeasureConditions";
import Filter from "./Filter";
import PropertyItemContainer from "./PropertyItemContainer";
import { PropertyName } from "./PropertyName";
import "./VariablePropertyItem.css";

interface Props {
  formulaNode: FormulaNode;
  state: CustomMeasureReturnType;
  editable: boolean;
  sx?: SxProps<Theme>;
  chartOfAccountItems?: ChartOfAccountsItem[];
}

export const VariablePropertyItem = ({ formulaNode, state, editable, sx, chartOfAccountItems }: Props) => {
  const formulaContainerRef = useRef<HTMLDivElement>(null);

  const conditions = useMemo(() => orderCustomMeasureConditions(formulaNode.conditions ?? []), [formulaNode.conditions]);
  const conditionsAreEmpty = conditions.length === 0;

  const isSelectedParameter = useMemo(() => {
    if (state.selectedFormulaParameter === undefined) return false;

    return formulaNode.linkedNodes.some((ln) => findNodeByTokenData(ln, state.selectedFormulaParameter));
  }, [state.selectedFormulaParameter, formulaNode]);

  useEffect(() => {
    if (isSelectedParameter) {
      formulaContainerRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isSelectedParameter]);

  return (
    <Grid container component="li" sx={{ flexWrap: "nowrap", width: "100%" }}>
      <Stack sx={{ flex: 1, width: "100%", ...sx }}>
        <PropertyItemContainer
          sx={formulaNode.highlighted === true ? { border: "1px solid", borderColor: "primary.main" } : {}}
          ref={formulaContainerRef}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between", minHeight: "30px", gap: 3, alignItems: "center" }}
          >
            <PropertyName
              sx={{
                px: 2,
                py: 0.5,
                "& .MuiTypography-root": { fontWeight: 700, wordBreak: "break-word" },
              }}
              text={formulaNode.text}
            />
            {editable && !conditionsAreEmpty && (
              <AddCustomMeasureConditions
                text="Add Conditions"
                conditions={formulaNode.conditions}
                dimensions={state.dimensions}
                onAddField={(newCondition) => state.actions.addConditions(formulaNode, [newCondition])}
                sx={(theme) => ({
                  height: "30px",
                  "& .MuiButton-text.MuiButtonBase-root.MuiButton-root": {
                    color: theme.palette.secondary.main,
                    px: 2,
                  },
                })}
              />
            )}
          </Box>
          {conditionsAreEmpty && (
            <>
              <Divider />
              <Grid
                container
                sx={{ px: 2, height: "30px", width: "100%", alignItems: "center", justifyContent: "space-between" }}
              >
                <Typography variant="caption">{getEmptyConditionsCaption(state.isConfigurable)}</Typography>
                {editable && (
                  <AddCustomMeasureConditions
                    text="Add Conditions"
                    conditions={conditions}
                    dimensions={state.dimensions}
                    onAddField={(newCondition) => state.actions.addConditions(formulaNode, [newCondition])}
                  />
                )}
              </Grid>
            </>
          )}
          {conditions.map((condition) => (
            <Fragment key={condition.meta.name}>
              <Divider />
              <Grid container sx={{ alignItems: "center", width: "100%" }}>
                <Filter
                  condition={condition}
                  conditions={conditions}
                  editable={editable}
                  onChanged={(changes) => state.actions.updateCondition(formulaNode, condition, changes)}
                  onRemove={() => state.actions.removeCondition(formulaNode, condition)}
                  chartOfAccountItems={chartOfAccountItems}
                />
              </Grid>
            </Fragment>
          ))}
        </PropertyItemContainer>
      </Stack>
    </Grid>
  );
};

export default VariablePropertyItem;
